import {
	AfterViewInit,
	Directive,
	effect,
	ElementRef,
	inject,
	input,
	OnDestroy,
	signal,
	untracked,
} from '@angular/core'
import { signalPersistence } from '@mos/client/shared/storage/signal-persistence'
import { StorageStrategy } from '@mos/client/shared/storage/strategies/storage.strategy'

export type ResizableBorder = 'top' | 'right' | 'bottom' | 'left'
export type ResizableDirection = 'horizontal' | 'vertical'
export type AllowedResizableDirection = 'all' | ResizableDirection | ResizableBorder

export const ALLOWED_RESIZABLE_DIRECTIONS_BORDERS: { [key in AllowedResizableDirection]: ResizableBorder[] } = {
	all: ['top', 'right', 'bottom', 'left'],
	horizontal: ['left', 'right'],
	vertical: ['top', 'bottom'],
	top: ['top'],
	right: ['right'],
	bottom: ['bottom'],
	left: ['left'],
}

export type ResizableState = {
	[key in ResizableDirection]?: number
}
export type ResizablePersistKey = string
export type ResizableProps = {
	[key in ResizablePersistKey]?: ResizableState
}

@Directive({
	selector: '[mosResizable]',
	host: {
		'(window:mouseup)': 'stopCursorControl()',
		'(window:mousemove)': 'updateCursorControl($event)',
	},
})
export class ResizableDirective implements OnDestroy, AfterViewInit {
	static store: ReturnType<typeof signal<ResizableProps>> | undefined

	static initStore(storage: StorageStrategy) {
		if (ResizableDirective.store) {
			return
		}

		const store = signal<ResizableProps>({})
		ResizableDirective.store = store

		signalPersistence<ResizableProps>({
			key: 'mos-admin-resizable',
			storage,
			read: () => store(),
			write: (stored) => store.set(stored),
			debounceMs: 100,
		})
	}

	protected readonly storageStrategy = inject(StorageStrategy)

	protected currentDirection?: ResizableDirection
	protected currentBorder?: HTMLDivElement
	protected mouseInitialPosition: { x: number; y: number } = { x: 0, y: 0 }
	protected borders: { [key in ResizableBorder]?: HTMLElement } = {}
	protected elementRef = inject<ElementRef<HTMLElement>>(ElementRef)

	readonly persistKey = input<ResizablePersistKey | undefined>(undefined)
	readonly mosResizableShow = input<boolean>(false)
	readonly mosResizableDefaultSize = input<number | undefined>(undefined)
	readonly mosResizableMinMax = input({ min: 10, max: 80 })
	readonly mosResizable = input.required<AllowedResizableDirection | AllowedResizableDirection[]>()

	constructor() {
		ResizableDirective.initStore(this.storageStrategy)

		effect(() => {
			this.loadState()
		})

		effect(() => {
			const value = this.mosResizableShow()
			for (const border of Object.values(this.borders)) {
				border.style.display = value ? 'block' : 'none'
			}
		})

		effect(() => {
			const value = this.mosResizableDefaultSize()
			if (value === undefined) return

			Object.keys(this.borders).forEach((border) => {
				const direction = border === 'top' || border === 'bottom' ? 'vertical' : 'horizontal'
				this.updateSize(direction, this.getSizeInState(direction) ?? value)
			})
		})

		effect(() => {
			const value = this.mosResizable()
			this.removeBorders()

			const directions = Array.isArray(value) ? value : [value]
			const borders = directions.reduce(
				(acc, direction) => [...acc, ...ALLOWED_RESIZABLE_DIRECTIONS_BORDERS[direction]],
				[] as ResizableBorder[],
			)

			borders.forEach((border) => {
				const borderElement = document.createElement('div')
				const direction = border === 'top' || border === 'bottom' ? 'vertical' : 'horizontal'

				borderElement.classList.add('mos-resizable-border', `mos-resizable-border-${border}`)
				borderElement.addEventListener('mousedown', (event) => this.startCursorControl(event, direction))

				this.elementRef.nativeElement.appendChild(borderElement)
				this.elementRef.nativeElement.classList.add(`mos-resizable-container`)
				this.borders[border] = borderElement

				const size = this.getSizeInState(direction) ?? untracked(() => this.mosResizableDefaultSize())
				if (size !== undefined) {
					this.updateSize(direction, size)
				}
			})
		})
	}

	get store() {
		// Always set by now: initStore() runs synchronously as the first line of the constructor, before any
		// instance method (which is all that ever reads this getter) can be called.
		return ResizableDirective.store!
	}

	removeBorders() {
		Object.values(this.borders).forEach((border) => border?.remove())
		this.borders = {}
	}

	ngOnDestroy() {
		this.removeBorders()
	}

	stopCursorControl() {
		this.currentDirection = undefined
		this.currentBorder?.classList.remove('mos-resizable-border-active')
		this.currentBorder = undefined
	}

	startCursorControl(event: MouseEvent, direction: ResizableDirection) {
		event.preventDefault()
		if (this.currentDirection) return

		this.currentDirection = direction
		this.currentBorder = event.target as HTMLDivElement
		this.mouseInitialPosition = { x: event.offsetX, y: event.offsetY }

		this.currentBorder.classList.add('mos-resizable-border-active')
		this.updateCursorControl(event)
	}

	updateCursorControl(event: MouseEvent) {
		if (!this.currentDirection) return

		const parent = this.elementRef.nativeElement.parentElement

		if (!parent) return
		let percent = 0

		// Resize in percent depending on the direction, and the initial position of the mouse, and the current position of the mouse
		if (this.currentDirection === 'vertical') {
			percent =
				((parent.offsetHeight -
					(event.clientY - this.mouseInitialPosition.y - parent.getBoundingClientRect().y)) /
					parent.offsetHeight) *
				100
		}

		if (this.currentDirection === 'horizontal') {
			percent =
				((parent.offsetWidth -
					(event.clientX - this.mouseInitialPosition.x - parent.getBoundingClientRect().x)) /
					parent.offsetWidth) *
				100
		}

		this.updateSize(this.currentDirection, percent)
	}

	updateSize(direction: ResizableDirection, percent: number) {
		const minMax = this.mosResizableMinMax()
		percent = Math.min(Math.max(percent, minMax.min), minMax.max)

		// Resize in percent depending on the direction, and the initial position of the mouse, and the current position of the mouse
		if (direction === 'vertical') {
			this.elementRef.nativeElement.style.height = `${percent}%`
		}

		if (direction === 'horizontal') {
			this.elementRef.nativeElement.style.width = `${percent}%`
		}

		this.persistState(direction, percent)
	}

	getState() {
		const key = this.persistKey()
		if (!key) return

		// Untracked: this must stay a plain snapshot read, not a reactive dependency - `persistState()` below
		// writes back to this same store, and `loadState()` runs inside an `effect()`, so a tracked read here
		// would make every write re-trigger the very effect that (indirectly) caused it.
		return untracked(() => this.store()[key]) ?? {}
	}

	getSizeInState(direction: ResizableDirection) {
		const state = this.getState()

		if (!state) return

		return state[direction]
	}

	loadState() {
		const state = this.getState()

		if (!state) return

		Object.entries(state).forEach(([direction, percent]) => {
			this.updateSize(direction as ResizableDirection, percent)
		})
	}

	persistState(direction: ResizableDirection, value: number) {
		const key = this.persistKey()
		if (!key) return

		this.store.update((state) => ({
			...state,
			[key]: {
				...state[key],
				[direction]: value,
			},
		}))
	}

	ngAfterViewInit() {
		this.loadState()
	}
}
